// MatchGeneratorController - Main business logic controller
// Updated to use iOS-style match generation algorithm
// Created by Taulant Geci

import { Player, Court, Match, AppState, SkillLevel, PlayerGroup, MatchHistory, MatchResult, PlayerStatistics, SortOrder, PlayerStats, MatchType, MatchGenerationResult, StatisticsFilter, FilteredStatistics, DetailedPlayerStatistics, PartnershipMatrixData, PlayerMatchStats, GenerationMode } from '../models/types';
import { IOSMatchGenerationService } from './IOSMatchGenerationService';

export class MatchGeneratorController {
  private state: AppState;
  private listeners: Set<(state: AppState) => void> = new Set();
  private iosMatchService: IOSMatchGenerationService;

  constructor() {
    this.iosMatchService = new IOSMatchGenerationService();
    this.state = this.getInitialState();
    this.loadFromStorage();
  }

  private getInitialState(): AppState {
    return {
      activePlayers: [],
      allPlayers: [],
      playerGroups: [],
      courts: [{ id: '1', number: 1, label: 'Court 1' }],
      generatedMatches: [],
      unmatchedPlayers: [],
      matchHistory: [],
      matchResults: [],
      playerStatistics: [],
      playerStats: new Map(),
      playerMatchStats: new Map(), // iOS-style detailed stats
      currentRound: 1, // Round tracking
      statisticsFilter: {
        showAllTime: true
      },
      selectedCourtCount: 1,
      isLoading: false,
      selectedGroupID: undefined,
      newGroupName: ''
    };
  }

  // State management
  subscribe(listener: (state: AppState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getState(): AppState {
    return { ...this.state };
  }

  private setState(updates: Partial<AppState>): void {
    this.state = { ...this.state, ...updates };
    this.saveToStorage();
    this.notifyListeners();
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.getState()));
  }

  // Player management
  addPlayer(name: string): boolean {
    if (!name.trim()) {
      return false;
    }

    // Check if player already exists
    if (this.state.allPlayers.some(p => p.name.toLowerCase() === name.trim().toLowerCase())) {
      return false;
    }

    const newPlayer: Player = {
      id: this.generateId(),
      name: name.trim(),
      skillLevel: SkillLevel.NOT_SET
    };

    // Initialize player stats for fairness tracking
    const newPlayerStats = new Map(this.state.playerStats);
    newPlayerStats.set(newPlayer.id, {
      partnerships: new Map(),
      pauseCount: 0,
      lastMatchType: MatchType.NONE,
      singlesCount: 0,
      doublesCount: 0,
      partnerHistory: new Map(),
      opponentHistory: new Map(),
      totalMatches: 0
    });

    this.setState({
      allPlayers: [...this.state.allPlayers, newPlayer],
      activePlayers: [...this.state.activePlayers, newPlayer],
      playerStats: newPlayerStats
    });

    return true;
  }

  removePlayer(playerId: string): void {
    // Remove from all players and active players
    this.setState({
      allPlayers: this.state.allPlayers.filter(p => p.id !== playerId),
      activePlayers: this.state.activePlayers.filter(p => p.id !== playerId)
    });

    // Remove from all groups
    const updatedGroups = this.state.playerGroups.map(group => ({
      ...group,
      playerIDs: new Set(Array.from(group.playerIDs).filter(id => id !== playerId))
    }));

    // Remove from player stats
    const newPlayerStats = new Map(this.state.playerStats);
    newPlayerStats.delete(playerId);
    // Also remove this player from all other players' partnership records
    newPlayerStats.forEach(stats => {
      stats.partnerships.delete(playerId);
    });

    this.setState({ 
      playerGroups: updatedGroups,
      playerStats: newPlayerStats
    });
  }

  togglePlayerActive(playerId: string): void {
    const player = this.state.allPlayers.find(p => p.id === playerId);
    if (!player) return;

    const isActive = this.state.activePlayers.some(p => p.id === playerId);
    
    if (isActive) {
      this.setState({
        activePlayers: this.state.activePlayers.filter(p => p.id !== playerId)
      });
    } else {
      this.setState({
        activePlayers: [...this.state.activePlayers, player]
      });
    }
  }

  updatePlayerSkill(playerId: string, skillLevel: SkillLevel): void {
    const updatedAllPlayers = this.state.allPlayers.map(p => 
      p.id === playerId ? { ...p, skillLevel } : p
    );
    
    const updatedActivePlayers = this.state.activePlayers.map(p => 
      p.id === playerId ? { ...p, skillLevel } : p
    );

    this.setState({
      allPlayers: updatedAllPlayers,
      activePlayers: updatedActivePlayers
    });
  }

  // PlayerGroup management
  createPlayerGroup(name: string): boolean {
    if (!name.trim()) {
      return false;
    }

    // Check if group already exists
    if (this.state.playerGroups.some(g => g.name.toLowerCase() === name.trim().toLowerCase())) {
      return false;
    }

    const newGroup: PlayerGroup = {
      id: this.generateId(),
      name: name.trim(),
      playerIDs: new Set()
    };

    this.setState({
      playerGroups: [...this.state.playerGroups, newGroup],
      newGroupName: ''
    });

    return true;
  }

  deletePlayerGroup(groupId: string): void {
    this.setState({
      playerGroups: this.state.playerGroups.filter(g => g.id !== groupId),
      selectedGroupID: this.state.selectedGroupID === groupId ? undefined : this.state.selectedGroupID
    });
  }

  addPlayerToGroup(playerId: string, groupId: string): void {
    const updatedGroups = this.state.playerGroups.map(group => {
      if (group.id === groupId) {
        const newPlayerIDs = new Set(group.playerIDs);
        newPlayerIDs.add(playerId);
        return { ...group, playerIDs: newPlayerIDs };
      }
      return group;
    });

    this.setState({ playerGroups: updatedGroups });
  }

  removePlayerFromGroup(playerId: string, groupId: string): void {
    const updatedGroups = this.state.playerGroups.map(group => {
      if (group.id === groupId) {
        const newPlayerIDs = new Set(group.playerIDs);
        newPlayerIDs.delete(playerId);
        return { ...group, playerIDs: newPlayerIDs };
      }
      return group;
    });

    this.setState({ playerGroups: updatedGroups });
  }

  isPlayerInGroup(playerId: string, groupId: string): boolean {
    const group = this.state.playerGroups.find(g => g.id === groupId);
    return group ? group.playerIDs.has(playerId) : false;
  }

  getPlayersInGroup(groupId: string): Player[] {
    const group = this.state.playerGroups.find(g => g.id === groupId);
    if (!group) return [];
    
    return this.state.allPlayers.filter(player => group.playerIDs.has(player.id));
  }

  selectGroup(groupId: string | undefined): void {
    this.setState({ selectedGroupID: groupId });
  }

  loadGroupPlayers(): boolean {
    if (!this.state.selectedGroupID) {
      return false;
    }

    const groupPlayers = this.getPlayersInGroup(this.state.selectedGroupID);
    
    this.setState({
      activePlayers: groupPlayers
    });

    return true;
  }

  setNewGroupName(name: string): void {
    this.setState({ newGroupName: name });
  }

  setCourtCount(count: number): void {
    const clampedCount = Math.max(1, Math.min(16, count));
    
    const newCourts: Court[] = [];
    for (let i = 1; i <= clampedCount; i++) {
      newCourts.push({
        id: i.toString(),
        number: i,
        label: `Court ${i}`
      });
    }

    this.setState({
      selectedCourtCount: clampedCount,
      courts: newCourts
    });
  }

  // iOS-style match generation with round tracking
  generateMatches(): boolean {
    if (this.state.activePlayers.length < 2) {
      return false;
    }

    console.log(`🎾 Generating matches for Round ${this.state.currentRound}`);

    // Use iOS-style generation service
    const result = this.iosMatchService.generate(
      this.state.activePlayers,
      this.state.courts,
      this.state.currentRound,
      false, // isDoublesOnly
      GenerationMode.BALANCED
    );

    // Save to match history with round information
    const currentGroup = this.state.selectedGroupID ? 
      this.state.playerGroups.find(g => g.id === this.state.selectedGroupID) : undefined;
      
    const newHistoryEntry: MatchHistory = {
      id: this.generateId(),
      matches: result.matches,
      timestamp: new Date(),
      unmatchedPlayers: result.unmatchedPlayers,
      groupId: currentGroup?.id,
      groupName: currentGroup?.name
    };

    console.log(`✅ Generated ${result.matches.length} matches, ${result.unmatchedPlayers.length} players resting`);
    console.log(`📊 Match Configuration: ${result.matchConfiguration.singlesPerRound} singles, ${result.matchConfiguration.doublesPerRound} doubles`);

    // Update state with new matches and incremented round
    this.setState({ 
      generatedMatches: result.matches,
      unmatchedPlayers: result.unmatchedPlayers,
      matchHistory: [newHistoryEntry, ...this.state.matchHistory],
      currentRound: this.state.currentRound + 1,
      playerMatchStats: this.iosMatchService.getPlayerMatchStats() // Sync iOS stats
    });

    return true;
  }

  // Reset matches and optionally reset round counter
  resetMatches(): void {
    this.setState({
      generatedMatches: [],
      unmatchedPlayers: []
    });
  }

  // Reset all history and statistics (like iOS)
  clearMatchHistory(): void {
    this.iosMatchService.resetPlayerStatistics();
    
    const newPlayerStats = new Map<string, PlayerStats>();
    const newPlayerMatchStats = new Map<string, PlayerMatchStats>();
    
    this.state.allPlayers.forEach(player => {
      newPlayerStats.set(player.id, {
        partnerships: new Map(),
        pauseCount: 0,
        lastMatchType: MatchType.NONE,
        singlesCount: 0,
        doublesCount: 0,
        partnerHistory: new Map(),
        opponentHistory: new Map(),
        totalMatches: 0
      });
      
      newPlayerMatchStats.set(player.id, {
        singlesCount: 0,
        doublesCount: 0,
        pauseCount: 0,
        partnerHistory: new Map(),
        opponentHistory: new Map(),
        lastMatchType: MatchType.NONE,
        lastRoundMatchType: MatchType.NONE,
        roundHistory: new Map(),
        pauseHistory: new Map(),
        totalMatches: 0
      });
    });

    this.setState({
      matchHistory: [],
      matchResults: [],
      playerStatistics: [],
      playerStats: newPlayerStats,
      playerMatchStats: newPlayerMatchStats,
      currentRound: 1 // Reset round counter
    });
  }

  // Get current round number
  getCurrentRound(): number {
    return this.state.currentRound;
  }

  // Get iOS-style player match statistics
  getIOSPlayerStats(): Map<string, PlayerMatchStats> {
    return this.state.playerMatchStats;
  }

  // History management
  getMatchHistory(): MatchHistory[] {
    return this.state.matchHistory;
  }

  // Match Results Management
  recordMatchResult(matchId: string, winnerIds: string[], loserIds: string[], winnerScore: number = 0, loserScore: number = 0): void {
    // Find the match to get group information
    const match = this.state.generatedMatches.find(m => m.id === matchId);
    const historyEntry = this.state.matchHistory.find(h => 
      h.matches.some(m => m.id === matchId)
    );
    
    const newResult: MatchResult = {
      id: this.generateId(),
      matchId,
      winnerIds,
      loserIds,
      winnerScore,
      loserScore,
      timestamp: new Date(),
      groupId: historyEntry?.groupId,
      groupName: historyEntry?.groupName
    };

    this.setState({
      matchResults: [...this.state.matchResults, newResult]
    });

    // Update player statistics
    this.updatePlayerStatistics(winnerIds, loserIds);
  }

  private updatePlayerStatistics(winnerIds: string[], loserIds: string[]): void {
    let updatedStatistics = [...this.state.playerStatistics];

    // Update winners
    for (const winnerId of winnerIds) {
      const existingIndex = updatedStatistics.findIndex(stat => stat.playerId === winnerId);
      if (existingIndex >= 0) {
        updatedStatistics[existingIndex] = {
          ...updatedStatistics[existingIndex],
          wins: updatedStatistics[existingIndex].wins + 1
        };
      } else {
        const player = this.state.allPlayers.find(p => p.id === winnerId);
        if (player) {
          updatedStatistics.push({
            playerId: winnerId,
            playerName: player.name,
            wins: 1,
            losses: 0,
            totalMatches: 1,
            winRate: 1.0,
            singlesCount: 0,
            doublesCount: 0,
            pauseCount: 0,
            partnerHistory: new Map(),
            opponentHistory: new Map()
          });
        }
      }
    }

    // Update losers
    for (const loserId of loserIds) {
      const existingIndex = updatedStatistics.findIndex(stat => stat.playerId === loserId);
      if (existingIndex >= 0) {
        updatedStatistics[existingIndex] = {
          ...updatedStatistics[existingIndex],
          losses: updatedStatistics[existingIndex].losses + 1
        };
      } else {
        const player = this.state.allPlayers.find(p => p.id === loserId);
        if (player) {
          updatedStatistics.push({
            playerId: loserId,
            playerName: player.name,
            wins: 0,
            losses: 1,
            totalMatches: 1,
            winRate: 0.0,
            singlesCount: 0,
            doublesCount: 0,
            pauseCount: 0,
            partnerHistory: new Map(),
            opponentHistory: new Map()
          });
        }
      }
    }

    // Recalculate totalMatches and winRate for updated stats
    updatedStatistics = updatedStatistics.map(stat => ({
      ...stat,
      totalMatches: stat.wins + stat.losses,
      winRate: stat.wins + stat.losses > 0 ? stat.wins / (stat.wins + stat.losses) : 0
    }));

    this.setState({
      playerStatistics: updatedStatistics
    });
  }

  getSortedPlayerStatistics(sortBy: SortOrder = SortOrder.WINS): PlayerStatistics[] {
    const stats = [...this.state.playerStatistics];
    
    switch (sortBy) {
      case SortOrder.WINS:
        return stats.sort((a, b) => b.wins - a.wins);
      case SortOrder.LOSSES:
        return stats.sort((a, b) => b.losses - a.losses);
      case SortOrder.WIN_RATE:
        return stats.sort((a, b) => b.winRate - a.winRate);
      case SortOrder.TOTAL_MATCHES:
        return stats.sort((a, b) => b.totalMatches - a.totalMatches);
      case SortOrder.NAME:
        return stats.sort((a, b) => a.playerName.localeCompare(b.playerName));
      default:
        return stats.sort((a, b) => b.wins - a.wins);
    }
  }

  getMatchResult(matchId: string): MatchResult | undefined {
    return this.state.matchResults.find(result => result.matchId === matchId);
  }

  // New: Statistics filtering methods
  setStatisticsFilter(filter: Partial<StatisticsFilter>): void {
    this.setState({
      statisticsFilter: { ...this.state.statisticsFilter, ...filter }
    });
  }

  getFilteredStatistics(): FilteredStatistics {
    const filter = this.state.statisticsFilter;
    let filteredResults = [...this.state.matchResults];

    // Filter by group
    if (filter.groupId) {
      filteredResults = filteredResults.filter(result => result.groupId === filter.groupId);
    }

    // Filter by date range
    if (!filter.showAllTime) {
      if (filter.dateFrom) {
        filteredResults = filteredResults.filter(result => 
          result.timestamp >= filter.dateFrom!
        );
      }
      if (filter.dateTo) {
        const endOfDay = new Date(filter.dateTo);
        endOfDay.setHours(23, 59, 59, 999);
        filteredResults = filteredResults.filter(result => 
          result.timestamp <= endOfDay
        );
      }
    }

    // Calculate statistics from filtered results
    const playerStatsMap = new Map<string, { wins: number, losses: number }>();
    
    filteredResults.forEach(result => {
      // Update winners
      result.winnerIds.forEach(winnerId => {
        const stats = playerStatsMap.get(winnerId) || { wins: 0, losses: 0 };
        stats.wins++;
        playerStatsMap.set(winnerId, stats);
      });
      
      // Update losers
      result.loserIds.forEach(loserId => {
        const stats = playerStatsMap.get(loserId) || { wins: 0, losses: 0 };
        stats.losses++;
        playerStatsMap.set(loserId, stats);
      });
    });

    // Convert to PlayerStatistics array
    const statistics: PlayerStatistics[] = [];
    playerStatsMap.forEach((stats, playerId) => {
      const player = this.state.allPlayers.find(p => p.id === playerId);
      if (player) {
        const totalMatches = stats.wins + stats.losses;
        statistics.push({
          playerId,
          playerName: player.name,
          wins: stats.wins,
          losses: stats.losses,
          totalMatches,
          winRate: totalMatches > 0 ? stats.wins / totalMatches : 0,
          singlesCount: 0,
          doublesCount: 0,
          pauseCount: 0,
          partnerHistory: new Map(),
          opponentHistory: new Map()
        });
      }
    });

    // Generate date range string
    let dateRange = '';
    if (filter.showAllTime) {
      dateRange = 'All Time';
    } else {
      if (filter.dateFrom && filter.dateTo) {
        dateRange = `${filter.dateFrom.toLocaleDateString()} - ${filter.dateTo.toLocaleDateString()}`;
      } else if (filter.dateFrom) {
        dateRange = `From ${filter.dateFrom.toLocaleDateString()}`;
      } else if (filter.dateTo) {
        dateRange = `Until ${filter.dateTo.toLocaleDateString()}`;
      }
    }

    // Get group name
    const groupName = filter.groupId ? 
      this.state.playerGroups.find(g => g.id === filter.groupId)?.name : undefined;

    return {
      statistics,
      totalMatches: filteredResults.length,
      dateRange,
      groupName
    };
  }

  getFilteredPlayerStatistics(sortBy: SortOrder = SortOrder.WIN_RATE): PlayerStatistics[] {
    const filter = this.state.statisticsFilter;
    let filteredHistory = [...this.state.matchHistory];
    let filteredResults = [...this.state.matchResults];

    // Apply same filters to match history and results
    if (filter.groupId) {
      filteredHistory = filteredHistory.filter(h => h.groupId === filter.groupId);
      filteredResults = filteredResults.filter(result => result.groupId === filter.groupId);
    }

    if (!filter.showAllTime) {
      if (filter.dateFrom) {
        filteredHistory = filteredHistory.filter(h => h.timestamp >= filter.dateFrom!);
        filteredResults = filteredResults.filter(result => result.timestamp >= filter.dateFrom!);
      }
      if (filter.dateTo) {
        const endOfDay = new Date(filter.dateTo);
        endOfDay.setHours(23, 59, 59, 999);
        filteredHistory = filteredHistory.filter(h => h.timestamp <= endOfDay);
        filteredResults = filteredResults.filter(result => result.timestamp <= endOfDay);
      }
    }

    // Calculate comprehensive statistics from filtered match history (iOS style)
    const playerStatsMap = new Map<string, {
      wins: number;
      losses: number;
      singlesCount: number;
      doublesCount: number;
      pauseCount: number;
      partnerHistory: Map<string, number>;
      opponentHistory: Map<string, number>;
    }>();

    // Initialize all players
    this.state.allPlayers.forEach(player => {
      playerStatsMap.set(player.id, {
        wins: 0,
        losses: 0,
        singlesCount: 0,
        doublesCount: 0,
        pauseCount: 0,
        partnerHistory: new Map(),
        opponentHistory: new Map()
      });
    });

    // Calculate statistics from filtered match history
    filteredHistory.forEach(historyEntry => {
      // Track pauses
      historyEntry.unmatchedPlayers.forEach(player => {
        const stats = playerStatsMap.get(player.id);
        if (stats) {
          stats.pauseCount++;
        }
      });

      // Track matches
      historyEntry.matches.forEach(match => {
        match.players.forEach(player => {
          const stats = playerStatsMap.get(player.id);
          if (stats) {
            if (match.players.length === 2) {
              // Singles match
              stats.singlesCount++;
              
              // Track opponent
              const opponent = match.players.find(p => p.id !== player.id);
              if (opponent) {
                const oppCount = stats.opponentHistory.get(opponent.id) || 0;
                stats.opponentHistory.set(opponent.id, oppCount + 1);
              }
            } else if (match.players.length === 4) {
              // Doubles match
              stats.doublesCount++;
              
              const playerIndex = match.players.findIndex(p => p.id === player.id);
              
              // Track partners and opponents
              match.players.forEach((otherPlayer, otherIndex) => {
                if (otherIndex !== playerIndex) {
                  const arePartners = (playerIndex < 2 && otherIndex < 2) || (playerIndex >= 2 && otherIndex >= 2);
                  
                  if (arePartners) {
                    const partnerCount = stats.partnerHistory.get(otherPlayer.id) || 0;
                    stats.partnerHistory.set(otherPlayer.id, partnerCount + 1);
                  } else {
                    const oppCount = stats.opponentHistory.get(otherPlayer.id) || 0;
                    stats.opponentHistory.set(otherPlayer.id, oppCount + 1);
                  }
                }
              });
            }
          }
        });
      });
    });

    // Add win/loss data from filtered match results
    filteredResults.forEach(result => {
      result.winnerIds.forEach(winnerId => {
        const stats = playerStatsMap.get(winnerId);
        if (stats) stats.wins++;
      });
      
      result.loserIds.forEach(loserId => {
        const stats = playerStatsMap.get(loserId);
        if (stats) stats.losses++;
      });
    });

    // Convert to PlayerStatistics array
    const statistics: PlayerStatistics[] = [];
    this.state.allPlayers.forEach(player => {
      const stats = playerStatsMap.get(player.id);
      if (stats) {
        const totalMatches = stats.wins + stats.losses;
        statistics.push({
          playerId: player.id,
          playerName: player.name,
          wins: stats.wins,
          losses: stats.losses,
          totalMatches,
          winRate: totalMatches > 0 ? stats.wins / totalMatches : 0,
          singlesCount: stats.singlesCount,
          doublesCount: stats.doublesCount,
          pauseCount: stats.pauseCount,
          partnerHistory: stats.partnerHistory,
          opponentHistory: stats.opponentHistory
        });
      }
    });

    // Sort statistics
    switch (sortBy) {
      case SortOrder.WINS:
        return statistics.sort((a, b) => b.wins - a.wins);
      case SortOrder.LOSSES:
        return statistics.sort((a, b) => b.losses - a.losses);
      case SortOrder.WIN_RATE:
        return statistics.sort((a, b) => b.winRate - a.winRate);
      case SortOrder.TOTAL_MATCHES:
        return statistics.sort((a, b) => (b.singlesCount + b.doublesCount) - (a.singlesCount + a.doublesCount));
      case SortOrder.NAME:
        return statistics.sort((a, b) => a.playerName.localeCompare(b.playerName));
      default:
        return statistics.sort((a, b) => b.winRate - a.winRate);
    }
  }

  // New: Enhanced statistics methods like iOS app
  getDetailedPlayerStatistics(): DetailedPlayerStatistics[] {
    return this.state.allPlayers.map(player => {
      const basicStats = this.getPlayerStatisticsFromHistory(player.id);
      const partnershipMatrix = this.calculatePartnershipMatrix(player.id);
      const playerStats = this.state.playerStats.get(player.id);
      
      return {
        ...basicStats,
        partnershipMatrix,
        lastMatchType: playerStats?.lastMatchType || MatchType.NONE
      };
    });
  }

  private getPlayerStatisticsFromHistory(playerId: string): PlayerStatistics {
    let singlesCount = 0;
    let doublesCount = 0;
    let pauseCount = 0;
    const partnerHistory = new Map<string, number>();
    const opponentHistory = new Map<string, number>();

    // Calculate from match history
    for (const historyEntry of this.state.matchHistory) {
      // Check if player is in unmatched (paused)
      if (historyEntry.unmatchedPlayers.some(p => p.id === playerId)) {
        pauseCount++;
      }

      // Check matches
      for (const match of historyEntry.matches) {
        const playerInMatch = match.players.find(p => p.id === playerId);
        if (playerInMatch) {
          if (match.players.length === 2) {
            singlesCount++;
            // Track opponent in singles
            const opponent = match.players.find(p => p.id !== playerId);
            if (opponent) {
              const currentCount = opponentHistory.get(opponent.id) || 0;
              opponentHistory.set(opponent.id, currentCount + 1);
            }
          } else if (match.players.length === 4) {
            doublesCount++;
            const playerIndex = match.players.findIndex(p => p.id === playerId);
            
            // Track partners and opponents in doubles
            for (let i = 0; i < match.players.length; i++) {
              if (i !== playerIndex) {
                const otherPlayer = match.players[i];
                const arePartners = (playerIndex < 2 && i < 2) || (playerIndex >= 2 && i >= 2);
                
                if (arePartners) {
                  const currentCount = partnerHistory.get(otherPlayer.id) || 0;
                  partnerHistory.set(otherPlayer.id, currentCount + 1);
                } else {
                  const currentCount = opponentHistory.get(otherPlayer.id) || 0;
                  opponentHistory.set(otherPlayer.id, currentCount + 1);
                }
              }
            }
          }
        }
      }
    }

    // Get win/loss stats
    const existingStats = this.state.playerStatistics.find(s => s.playerId === playerId);
    const player = this.state.allPlayers.find(p => p.id === playerId);
    
    return {
      playerId,
      playerName: player?.name || 'Unknown',
      wins: existingStats?.wins || 0,
      losses: existingStats?.losses || 0,
      totalMatches: singlesCount + doublesCount,
      winRate: existingStats?.winRate || 0,
      singlesCount,
      doublesCount,
      pauseCount,
      partnerHistory,
      opponentHistory
    };
  }

  private calculatePartnershipMatrix(playerId: string): PartnershipMatrixData[] {
    const matrix: PartnershipMatrixData[] = [];
    const playerStats = this.getPlayerStatisticsFromHistory(playerId);

    for (const otherPlayer of this.state.allPlayers) {
      if (otherPlayer.id !== playerId) {
        const partnershipCount = playerStats.partnerHistory.get(otherPlayer.id) || 0;
        const opponentCount = playerStats.opponentHistory.get(otherPlayer.id) || 0;
        
        matrix.push({
          playerId1: playerId,
          playerId2: otherPlayer.id,
          partnershipCount,
          opponentCount,
          totalInteractions: partnershipCount + opponentCount
        });
      }
    }

    return matrix;
  }

  // Get partnership matrix for all players (like iOS app)
  getPartnershipMatrix(): Map<string, Map<string, number>> {
    const matrix = new Map<string, Map<string, number>>();
    
    for (const player of this.state.allPlayers) {
      const playerStats = this.getPlayerStatisticsFromHistory(player.id);
      matrix.set(player.id, playerStats.partnerHistory);
    }
    
    return matrix;
  }

  // Storage
  private saveToStorage(): void {
    try {
      // Convert Maps and Sets to Arrays for JSON serialization
      const stateToSave = {
        ...this.state,
        playerGroups: this.state.playerGroups.map(group => ({
          ...group,
          playerIDs: Array.from(group.playerIDs)
        })),
        playerStats: Array.from(this.state.playerStats.entries()).map(([playerId, stats]) => ({
          playerId,
          partnerships: Array.from(stats.partnerships.entries()),
          pauseCount: stats.pauseCount,
          lastMatchType: stats.lastMatchType,
          singlesCount: stats.singlesCount,
          doublesCount: stats.doublesCount,
          partnerHistory: Array.from(stats.partnerHistory.entries()),
          opponentHistory: Array.from(stats.opponentHistory.entries()),
          totalMatches: stats.totalMatches
        })),
        // iOS-style player match stats
        playerMatchStats: Array.from(this.state.playerMatchStats.entries()).map(([playerId, stats]) => ({
          playerId,
          singlesCount: stats.singlesCount,
          doublesCount: stats.doublesCount,
          pauseCount: stats.pauseCount,
          partnerHistory: Array.from(stats.partnerHistory.entries()),
          opponentHistory: Array.from(stats.opponentHistory.entries()),
          lastMatchType: stats.lastMatchType,
          lastRoundMatchType: stats.lastRoundMatchType,
          roundHistory: Array.from(stats.roundHistory.entries()),
          pauseHistory: Array.from(stats.pauseHistory.entries()),
          totalMatches: stats.totalMatches
        })),
        // Convert dates to ISO strings for proper serialization
        matchHistory: this.state.matchHistory.map(entry => ({
          ...entry,
          timestamp: entry.timestamp.toISOString()
        })),
        matchResults: this.state.matchResults.map(result => ({
          ...result,
          timestamp: result.timestamp.toISOString()
        })),
        statisticsFilter: {
          ...this.state.statisticsFilter,
          dateFrom: this.state.statisticsFilter.dateFrom?.toISOString(),
          dateTo: this.state.statisticsFilter.dateTo?.toISOString()
        }
      };
      localStorage.setItem('courtmate-state', JSON.stringify(stateToSave));
    } catch (error) {
      console.error('Failed to save state:', error);
    }
  }

  private loadFromStorage(): void {
    try {
      const savedState = localStorage.getItem('courtmate-state');
      if (savedState) {
        const loadedState = JSON.parse(savedState);
        
        // Convert arrays back to Maps and Sets
        const playerGroups = (loadedState.playerGroups || []).map((group: any) => ({
          ...group,
          playerIDs: new Set(group.playerIDs || [])
        }));

        const playerStats = new Map();
        (loadedState.playerStats || []).forEach((playerData: any) => {
          playerStats.set(playerData.playerId, {
            partnerships: new Map(playerData.partnerships || []),
            pauseCount: playerData.pauseCount || 0,
            lastMatchType: playerData.lastMatchType || MatchType.NONE,
            singlesCount: playerData.singlesCount || 0,
            doublesCount: playerData.doublesCount || 0,
            partnerHistory: new Map(playerData.partnerHistory || []),
            opponentHistory: new Map(playerData.opponentHistory || []),
            totalMatches: playerData.totalMatches || 0
          });
        });

        // iOS-style player match stats
        const playerMatchStats = new Map();
        (loadedState.playerMatchStats || []).forEach((playerData: any) => {
          playerMatchStats.set(playerData.playerId, {
            singlesCount: playerData.singlesCount || 0,
            doublesCount: playerData.doublesCount || 0,
            pauseCount: playerData.pauseCount || 0,
            partnerHistory: new Map(playerData.partnerHistory || []),
            opponentHistory: new Map(playerData.opponentHistory || []),
            lastMatchType: playerData.lastMatchType || MatchType.NONE,
            lastRoundMatchType: playerData.lastRoundMatchType || MatchType.NONE,
            roundHistory: new Map(playerData.roundHistory || []),
            pauseHistory: new Map(playerData.pauseHistory || []),
            totalMatches: playerData.totalMatches || 0
          });
        });

        // Convert dates back to Date objects
        const matchHistory = (loadedState.matchHistory || []).map((entry: any) => ({
          ...entry,
          timestamp: new Date(entry.timestamp)
        }));

        const matchResults = (loadedState.matchResults || []).map((result: any) => ({
          ...result,
          timestamp: new Date(result.timestamp)
        }));

        // Convert statisticsFilter dates back to Date objects
        const statisticsFilter = {
          showAllTime: true,
          ...loadedState.statisticsFilter,
          dateFrom: loadedState.statisticsFilter?.dateFrom ? 
            new Date(loadedState.statisticsFilter.dateFrom) : undefined,
          dateTo: loadedState.statisticsFilter?.dateTo ? 
            new Date(loadedState.statisticsFilter.dateTo) : undefined
        };

        this.setState({
          ...loadedState,
          playerGroups,
          playerStats,
          playerMatchStats, // iOS-style stats
          matchHistory,
          matchResults,
          statisticsFilter,
          currentRound: loadedState.currentRound || 1 // Load round number
        });

        // Sync iOS service with loaded stats
        if (playerMatchStats.size > 0) {
          // Note: We can't directly set the iOS service stats from storage
          // The service will rebuild its stats as matches are generated
          console.log('📊 Loaded iOS-style player match statistics for', playerMatchStats.size, 'players');
        }
      }
    } catch (error) {
      console.error('Failed to load state:', error);
    }
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Drag & Drop Support - Player Swap Functions
  
  /**
   * Swaps two players between matches or within the same match
   */
  swapPlayers(
    sourceMatchId: string, 
    sourcePlayerIndex: number, 
    targetMatchId: string, 
    targetPlayerIndex: number
  ): boolean {
    const sourceMatch = this.state.generatedMatches.find(m => m.id === sourceMatchId);
    const targetMatch = this.state.generatedMatches.find(m => m.id === targetMatchId);
    
    if (!sourceMatch || !targetMatch || 
        sourcePlayerIndex >= sourceMatch.players.length || 
        targetPlayerIndex >= targetMatch.players.length ||
        sourcePlayerIndex < 0 || targetPlayerIndex < 0) {
      return false;
    }
    
    // Don't allow dropping on same position
    if (sourceMatchId === targetMatchId && sourcePlayerIndex === targetPlayerIndex) {
      return false;
    }
    
    // Perform the swap
    const sourcePlayer = sourceMatch.players[sourcePlayerIndex];
    const targetPlayer = targetMatch.players[targetPlayerIndex];
    
    sourceMatch.players[sourcePlayerIndex] = targetPlayer;
    targetMatch.players[targetPlayerIndex] = sourcePlayer;
    
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }
  
  /**
   * Swaps a match player with an unmatched (resting) player
   */
  swapPlayerWithUnmatched(
    matchId: string, 
    matchPlayerIndex: number, 
    unmatchedPlayerIndex: number
  ): boolean {
    const match = this.state.generatedMatches.find(m => m.id === matchId);
    
    if (!match || 
        matchPlayerIndex >= match.players.length || 
        unmatchedPlayerIndex >= this.state.unmatchedPlayers.length ||
        matchPlayerIndex < 0 || unmatchedPlayerIndex < 0) {
      return false;
    }
    
    // Perform the swap
    const matchPlayer = match.players[matchPlayerIndex];
    const unmatchedPlayer = this.state.unmatchedPlayers[unmatchedPlayerIndex];
    
    match.players[matchPlayerIndex] = unmatchedPlayer;
    this.state.unmatchedPlayers[unmatchedPlayerIndex] = matchPlayer;
    
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }
  
  /**
   * Swaps two unmatched (resting) players
   */
  swapUnmatchedPlayers(
    sourceIndex: number, 
    targetIndex: number
  ): boolean {
    if (sourceIndex >= this.state.unmatchedPlayers.length || 
        targetIndex >= this.state.unmatchedPlayers.length ||
        sourceIndex < 0 || targetIndex < 0 || 
        sourceIndex === targetIndex) {
      return false;
    }
    
    // Perform the swap
    const sourcePlayer = this.state.unmatchedPlayers[sourceIndex];
    const targetPlayer = this.state.unmatchedPlayers[targetIndex];
    
    this.state.unmatchedPlayers[sourceIndex] = targetPlayer;
    this.state.unmatchedPlayers[targetIndex] = sourcePlayer;
    
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }
} 